import classNames from "./EventInterpreter.module.css";
import { useEffect, useRef } from "react";
import { LiveKitRoom } from "@livekit/components-react";
import useUser, { useUserMetadata } from "../../../hooks/user";
import Loader from "../../common/Loader";
import { usePublisherToken } from "../../../hooks/token";
import { useAxios } from "../../../hooks/axios";
import { useRoom, useWakeLock } from "../../context/room/room";
import { Room } from "../../../hooks/api-new";
import UserLanguageSelect from "../../common/UserLanguageSelect";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import EventTitles from "./EventTitles";
import { useAudioEnabled } from "../../../hooks/media";
import Button from "../../common/Button";
import SettingsDialog from "../../common/dialogs/SettingsDialog";
import { IoEllipsisHorizontal, IoMicOutline } from "react-icons/io5";
import ConfirmLeaveDialog from "../../common/dialogs/ConfirmLeaveDialog";
import interpreterWaveformImg from "../../../assets/event/interpreter-waveform.svg";
import ToggleMicDialog from "./ToggleMicDialog";
import PinDialog from "./PinDialog";
import { useIsMobile } from "../../../hooks/layout";

function Dashboard(props: { room: Room }) {
  // Sync metadata with livekit
  useUserMetadata();

  useWakeLock();

  const [audioEnabled, setAudioEnabled] = useAudioEnabled();

  // Set audio initial state to false
  const setInitialAudioState = useRef(false);
  useEffect(() => {
    if (!setInitialAudioState.current) {
      setInitialAudioState.current = true;
      setAudioEnabled(false);
    }
  }, [setAudioEnabled]);

  const isMobile = useIsMobile();

  return (
    <div className={classNames.EventInterpreter}>
      <div className={classNames.Dashboard}>
        <EventTitles room={props.room} />
        <img src={interpreterWaveformImg} alt="Waveform" />
      </div>

      <div className={classNames.Controls}>
        <UserLanguageSelect languages={props.room.languages} />

        <div className={classNames.MicToggle}>
          <h2>Interpretation {audioEnabled ? "on" : "off"}</h2>
          <ToggleMicDialog
            title={`Turn ${!audioEnabled ? "on" : "off"} interpretation`}
            description={
              !audioEnabled
                ? "Are you sure you want to start the interpretation for your attendees? They will hear interpretation in real-time as you speak."
                : "Are you sure you want to stop the interpretation for your attendees? They will no longer hear interpretation in real-time as you speak."
            }
          />
        </div>

        <div>
          <SettingsDialog
            hiddenCategories={[
              "video",
              "voice-avatar",
              "text-customization",
              "text-speed",
              "text-translation",
            ]}
            trigger={
              <Button
                color="gray"
                borderColor="dark-gray"
                backgroundColor="black"
              >
                {!isMobile ? <IoMicOutline /> : <IoEllipsisHorizontal />}
                {!isMobile && "Audio settings"}
              </Button>
            }
          />
          <ConfirmLeaveDialog link={`/event/${props.room.id}/survey`} />
        </div>
      </div>
    </div>
  );
}

function EventInterpreter() {
  const [, setUser] = useUser();
  const axios = useAxios();
  const { token, error: tokenError } = usePublisherToken(axios);

  const { room, loading } = useRoom();

  useEffect(() => {
    setUser((prevState) => ({
      ...prevState,
      metadata: {
        ...prevState.metadata,
        participantType: "interpreter",
      },
    }));
    return () => {
      setUser((prevState) => ({
        ...prevState,
        metadata: {
          ...prevState.metadata,
          participantType: undefined,
        },
      }));
    };
  }, [setUser]);

  const navigate = useNavigate();
  useEffect(() => {
    if (!room) {
      return;
    }
    if (tokenError && tokenError.response?.data.message === "EVENT_ENDED") {
      toast(
        <div className="Toast">
          <div>{"This event has ended, thanks for joining us"}</div>
        </div>
      );
      navigate(`/event/${room.id}/survey`);
      return;
    }
    if (
      tokenError &&
      tokenError.response?.data.message === "EVENT_NOT_STARTED"
    ) {
      toast(
        <div className="Toast">
          <div>{" This event hasn't started yet, stay tuned"}</div>
        </div>
      );
      navigate(`/`);
      return;
    }
  }, [tokenError, navigate, room]);

  const [searchParams, setSearchParams] = useSearchParams();

  if (!room || !token || loading) {
    return <Loader />;
  }

  // TODO: Get room pin (if not in url), use in token issuance
  if (!searchParams.has("pin")) {
    return (
      <PinDialog
        onSubmit={(pin) =>
          setSearchParams((prevState) => {
            prevState.set("pin", pin);
            return prevState;
          })
        }
      />
    );
  }

  return (
    <>
      <LiveKitRoom
        audio={false}
        video={false}
        token={token}
        connectOptions={{ autoSubscribe: false }}
        connect={true}
        serverUrl={import.meta.env.VITE_LIVEKIT_URL}
        style={{
          width: "100%",
          height: "fit-content",
        }}
      >
        <Dashboard room={room} />
      </LiveKitRoom>
    </>
  );
}

export default EventInterpreter;
